// src/components/auth/ResetPasswordPage.tsx
import React, { useState, useEffect } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LockRounded } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import logo from '@/assets/images/roommatik_logo.webp';
import { API_CONFIG } from '@/config/constants';

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.custom.login.containerGradient(theme.palette),
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const LoginBox = styled(Paper)(({ theme }) => ({
  width: '90%',
  maxWidth: 440,
  borderRadius: theme.custom.radius.large,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.custom.shadows.login,
  position: 'relative',
  borderTop: `8px solid ${theme.palette.primary.light}`,
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.custom.radius.large,
    maxWidth: '90%',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(8, 4, 4),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3, 3),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 6, 6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 3, 4),
  },
}));

interface TokenValidation {
  isValid: boolean;
  errorMessage?: string;
  email?: string;
}

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValidation, setTokenValidation] = useState<TokenValidation | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const token = searchParams.get('token');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValidation({
          isValid: false,
          errorMessage: translate('pages.reset_password.no_token'),
        });
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/validate-reset-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const result = await response.json();

        if (response.ok && result.data) {
          setTokenValidation(result.data);
        } else {
          setTokenValidation({
            isValid: false,
            errorMessage: translate('pages.reset_password.invalid_token'),
          });
        }
      } catch (error) {
        setTokenValidation({
          isValid: false,
          errorMessage: translate('pages.reset_password.validation_error'),
        });
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, translate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      notify(translate('pages.reset_password.passwords_dont_match'), { type: 'warning' });
      return;
    }

    if (password.length < 6) {
      notify(translate('pages.reset_password.password_too_short'), { type: 'warning' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/reset-password-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResetSuccess(true);
        notify(translate('pages.reset_password.success'), { type: 'success' });
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        notify(data.error?.message || translate('pages.reset_password.error'), {
          type: 'error',
        });
      }
    } catch (error) {
      notify(translate('pages.reset_password.error'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const isFormValid = password.length >= 6 && password === confirmPassword;

  if (isValidating) {
    return (
      <LoginContainer>
        <LoginBox>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {translate('pages.reset_password.validating')}
            </Typography>
          </Box>
        </LoginBox>
      </LoginContainer>
    );
  }

  if (!tokenValidation?.isValid) {
    return (
      <LoginContainer>
        <LoginBox>
          <LogoContainer>
            <Box
              component="img"
              src={logo}
              alt="Logo de Roommatik"
              sx={{
                width: isMobile ? 120 : 380,
                height: 'auto',
                maxHeight: 90,
                mb: 4,
                filter: theme.custom.login.logoFilter,
                objectFit: 'contain',
              }}
            />
          </LogoContainer>
          <FormContainer>
            <Alert severity="error" sx={{ mb: 3 }}>
              {tokenValidation?.errorMessage || translate('pages.reset_password.invalid_token')}
            </Alert>
            <Button
              variant="contained"
              onClick={handleBackToLogin}
              sx={{
                width: '100%',
                py: 1.5,
                textTransform: 'none',
              }}
            >
              {translate('pages.reset_password.back_to_login')}
            </Button>
          </FormContainer>
        </LoginBox>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <LoginBox>
        <LogoContainer>
          <Box
            component="img"
            src={logo}
            alt="Logo de Roommatik"
            sx={{
              width: isMobile ? 120 : 380,
              height: 'auto',
              maxHeight: 90,
              mb: 4,
              filter: theme.custom.login.logoFilter,
              objectFit: 'contain',
            }}
          />
          <Typography variant={isMobile ? "h5" : "h3"}>
            {translate('pages.reset_password.title')}
          </Typography>
          {tokenValidation.email && (
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
              {tokenValidation.email}
            </Typography>
          )}
        </LogoContainer>

        <FormContainer>
          {resetSuccess ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="success">
                {translate('pages.reset_password.success_message')}
              </Alert>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {translate('pages.reset_password.redirecting')}
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <TextField
                label={translate('pages.reset_password.new_password')}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                disabled={isLoading}
                size='medium'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                        sx={{ border: 'none', background: 'none', color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: isMobile ? '100%' : '90%',
                  mb: isMobile ? 2 : 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.custom.login.fieldBackground(theme.palette),
                  },
                }}
              />

              <TextField
                label={translate('pages.reset_password.confirm_password')}
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isLoading}
                size='medium'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        disabled={isLoading}
                        sx={{ border: 'none', background: 'none', color: theme.palette.text.secondary }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: isMobile ? '100%' : '90%',
                  mb: isMobile ? 2 : 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.custom.login.fieldBackground(theme.palette),
                  },
                }}
              />

              <Button
                variant="contained"
                type="submit"
                disabled={isLoading || !isFormValid}
                sx={{
                  width: isMobile ? '100%' : '100%',
                  mt: isMobile ? 1 : 2,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                {isLoading
                  ? translate('pages.reset_password.resetting')
                  : translate('pages.reset_password.submit_button')
                }
              </Button>

              <Button
                variant="text"
                onClick={handleBackToLogin}
                disabled={isLoading}
                sx={{
                  mt: 2,
                  textTransform: 'none',
                }}
              >
                {translate('pages.reset_password.back_to_login')}
              </Button>
            </Box>
          )}
        </FormContainer>
      </LoginBox>
    </LoginContainer>
  );
};

export default ResetPasswordPage;
